// src/pages/products/ProductsListPage/ProductsListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import ProductsAPI from '../../../api/v3/products-api-v3';

import ManualDeleteButton, { MANUAL_DELETE_NOTE } from '../../../components/bagspa/ManualDeleteButton';

// ---------- helpers ----------
function includesCi(haystack = '', needle = '') {
  return (haystack || '').toString().toLowerCase().includes((needle || '').toString().toLowerCase());
}
function toDateMs(v) {
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}

// ---- size helpers for list ----
function cmLabelFor(kind, min_cm, max_cm) {
  if (kind !== 'bag') return '';
  if (min_cm && max_cm) return `${min_cm}-${max_cm} cm`;
  if (min_cm && !max_cm) return `≥${min_cm} cm`;
  if (!min_cm && max_cm) return `≤${max_cm} cm`;
  return '';
}
function renderSizeCell(p) {
  // Prefer the detailed label sent by backend, fall back to code/name.
  const main = p.size_label || p.size_name || p.size_code || p.size_id || '-';

  const cm   = cmLabelFor(p.size_kind, p.size_min_cm, p.size_max_cm);
  const dim  = p.size_dimension || p.size_dimension_text || null;
  const kind = p.size_kind || null;

  const bits = [cm || null, kind, dim].filter(Boolean);
  return bits.length ? `${main} · ${bits.join(' • ')}` : main;
}

// ---- export helpers (XLSX via CDN like Orders page) ----
async function ensureXLSX() {
  if (window.XLSX) return window.XLSX;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js';
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.XLSX;
}

const EXPORT_HEADERS = [
  'SKU',
  'Customer',
  'Brand',
  'Category',
  'Colour',
  'Colour Hex',
  'Size (label)',
  'Size Kind',
  'Size Min (cm)',
  'Size Max (cm)',
  'Size Dimension',
  'Material',
  'Material Code',
  'Created At (Date)'
];

export default function ProductsListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState({ key: 'sku', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(10);

  // date range (server + client safety)
  const [createdFrom, setCreatedFrom] = useState(''); // YYYY-MM-DD
  const [createdTo,   setCreatedTo]   = useState(''); // YYYY-MM-DD

  // export busy flag
  const [exporting, setExporting] = useState(false);

  async function load() {
    try {
      setLoading(true);
      setHydrating(true);
      setError('');

      const params = { per: 500 }; // pull enough for on-page filtering + export
      // pass q for optional backend narrowing (matches SKU only; FE still does broader client filter)
      if (q) params.q = q;
      if (createdFrom) params.created_from = createdFrom;
      if (createdTo)   params.created_to   = createdTo;
      try { params.tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) {}

      const json = await ProductsAPI.list(params);
      const list = Array.isArray(json) ? json : (json?.items || json?.data || []);
      setRows(list);
      setPage(1);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setHydrating(false);
      setLoading(false);
    }
  }

  // initial load
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // ---------- filter (client) ----------
  const filtered = useMemo(() => {
    let arr = rows;

    // client safety for date range
    if (createdFrom) {
      const fromMs = Date.parse(`${createdFrom}T00:00:00`);
      arr = arr.filter(p => toDateMs(p.created_at) >= fromMs);
    }
    if (createdTo) {
      const toMs = Date.parse(`${createdTo}T23:59:59.999`);
      arr = arr.filter(p => toDateMs(p.created_at) <= toMs);
    }

    if (!q) return arr;

    // Keep your broad client-side search (SKU/Brand/Category/Colour/Size/Material/Customer)
    return arr.filter((p) => {
      const sizeTexts = [
        p.size_label,
        p.size_dimension,
        p.size_dimension_text,
        p.size_kind,
        p.size_name,
        p.size_code,
        p.size_min_cm,
        p.size_max_cm,
      ]
        .filter((v) => v !== undefined && v !== null)
        .join(' ');
      return (
        includesCi(p.sku, q) ||
        includesCi(p.customer_name || p.customer_id, q) ||
        includesCi(p.brand_name || p.brand_id, q) ||
        includesCi(p.category_name || p.category_id, q) ||
        includesCi(p.colour_name || p.colour_id, q) ||
        includesCi(sizeTexts, q) ||
        includesCi(p.material_name || p.material_id || p.material_code, q)
      );
    });
  }, [rows, q, createdFrom, createdTo]);

  // ---------- sort ----------
  const sorted = useMemo(() => {
    const copy = [...filtered];
    const { key, dir } = sortBy;

    copy.sort((a, b) => {
      let av;
      let bv;

      switch (key) {
        case 'created_at': {
          av = toDateMs(a.created_at);
          bv = toDateMs(b.created_at);
          break;
        }
        case 'size': {
          const aMin = a.size_min_cm ?? Number.NEGATIVE_INFINITY;
          const bMin = b.size_min_cm ?? Number.NEGATIVE_INFINITY;
          if (aMin !== bMin) return dir === 'asc' ? (aMin - bMin) : (bMin - aMin);

          const aMax = a.size_max_cm ?? Number.POSITIVE_INFINITY;
          const bMax = b.size_max_cm ?? Number.POSITIVE_INFINITY;
          if (aMax !== bMax) return dir === 'asc' ? (aMax - bMax) : (bMax - aMax);

          av = (a.size_label || a.size_name || a.size_code || '').toString().toLowerCase();
          bv = (b.size_label || b.size_name || b.size_code || '').toString().toLowerCase();
          break;
        }
        default: {
          av = (a[key] ?? '').toString().toLowerCase();
          bv = (b[key] ?? '').toString().toLowerCase();
        }
      }

      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ?  1 : -1;
      return 0;
    });

    return copy;
  }, [filtered, sortBy]);

  // ---------- pagination ----------
  const totalPages = Math.max(1, Math.ceil(sorted.length / per));
  const pageSafe = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (pageSafe - 1) * per;
    return sorted.slice(start, start + per);
  }, [sorted, pageSafe, per]);

  function toggleSort(key) {
    setSortBy(prev => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  }

  // ---------- Export (current filtered+sorted) ----------
  function rowsToExport() {
    return sorted;
  }

  function val(v) { return v == null ? '' : v; }

  async function exportCSV() {
    try {
      setExporting(true);
      const rows = rowsToExport();

      const esc = (s) => {
        const str = String(s ?? '');
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };

      const lines = [
        EXPORT_HEADERS.join(','),
        ...rows.map((p) =>
          [
            p.sku,
            p.customer_name || p.customer_id || '',
            p.brand_name || p.brand_id || '',
            p.category_name || p.category_id || '',
            p.colour_name || p.colour_id || '',
            p.colour_hex || '',
            p.size_label || p.size_name || p.size_code || p.size_id || '',
            p.size_kind || '',
            val(p.size_min_cm),
            val(p.size_max_cm),
            p.size_dimension || p.size_dimension_text || '',
            p.material_name || p.material_id || '',
            p.material_code || '',
            p.created_at ? dayjs(p.created_at).format('YYYY-MM-DD') : ''
          ].map(esc).join(',')
        ),
      ];

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  async function exportXLSX() {
    try {
      setExporting(true);
      const rows = rowsToExport();
      const XLSX = await ensureXLSX();

      const aoa = [
        EXPORT_HEADERS,
        ...rows.map((p) => [
          p.sku,
          p.customer_name || p.customer_id || '',
          p.brand_name || p.brand_id || '',
          p.category_name || p.category_id || '',
          p.colour_name || p.colour_id || '',
          p.colour_hex || '',
          p.size_label || p.size_name || p.size_code || p.size_id || '',
          p.size_kind || '',
          p.size_min_cm ?? '',
          p.size_max_cm ?? '',
          p.size_dimension || p.size_dimension_text || '',
          p.material_name || p.material_id || '',
          p.material_code || '',
          p.created_at ? dayjs(p.created_at).format('YYYY-MM-DD') : ''
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      XLSX.writeFile(wb, `products_export_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  if (loading) return <div>Loading…</div>;
  if (error)   return <div className="text-danger">{error}</div>;

  return (
    <div className="bagspa-page">
      {/* Header */}
      <div className="bagspa-page-header products-page-header">
        <div>
          <h2 className="mb-0">Products</h2>
        </div>
      </div>

      {/* Controls */}
      <div className="bagspa-filter-card products-control-card">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          <div className="products-control-top-row">
            <div className="bagspa-filter-field products-filter-search">
              <label className="form-label">Search</label>
              <div className="input-group products-search-group">
                <input
                  className="form-control"
                  placeholder="Search SKU / Brand / Category / Colour / Size…"
                  value={q}
                  onChange={(e) => {
                    setPage(1);
                    setQ(e.target.value);
                  }}
                />
                <div className="input-group-append">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={hydrating}
                  >
                    {hydrating ? 'Loading…' : 'Search'}
                  </button>
                </div>
              </div>
            </div>

            <div className="products-date-group">
              <div className="bagspa-filter-field products-filter-from">
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={createdFrom}
                  onChange={(e) => setCreatedFrom(e.target.value)}
                  aria-label="Created from"
                  title="Created from"
                />
              </div>

              <div className="bagspa-filter-field products-filter-to">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={createdTo}
                  onChange={(e) => setCreatedTo(e.target.value)}
                  aria-label="Created to"
                  title="Created to"
                />
              </div>
            </div>

            <div className="bagspa-filter-field products-filter-rows">
              <label className="form-label">Rows</label>
              <select
                className="form-control products-per-select"
                value={per}
                onChange={(e) => {
                  setPage(1);
                  setPer(parseInt(e.target.value, 10));
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
            </div>

            <div className="bagspa-filter-actions products-filter-apply">
              <button
                type="button"
                className="btn btn-primary"
                onClick={load}
                disabled={hydrating}
              >
                {hydrating ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        </form>

        <div className="text-muted small products-showing-text">
          Showing {paged.length} of {sorted.length} items
        </div>

        <div className="products-control-bottom-row">
          <button
            type="button"
            className="btn btn-outline-primary bagspa-btn-outline"
            onClick={exportCSV}
            disabled={exporting || sorted.length === 0}
          >
            {exporting ? 'Preparing…' : 'Export CSV'}
          </button>

          <button
            type="button"
            className="btn btn-outline-primary bagspa-btn-outline"
            onClick={exportXLSX}
            disabled={exporting || sorted.length === 0}
          >
            {exporting ? 'Preparing…' : 'Export XLSX'}
          </button>

          <Link to="/app/sales/bagspa/products/new" className="btn btn-primary">
            + New Product
          </Link>
        </div>
      </div>

      <p className="text-muted small">{MANUAL_DELETE_NOTE}</p>

      <div className="bagspa-table-card mt-3">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle products-table">
            <thead className="table-light">
              <tr>
                <th role="button" onClick={() => toggleSort('sku')}>
                  SKU {sortBy.key === 'sku' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('customer_name')}>
                  Customer {sortBy.key === 'customer_name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('brand_name')}>
                  Brand {sortBy.key === 'brand_name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('category_name')}>
                  Category {sortBy.key === 'category_name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('colour_name')}>
                  Colour {sortBy.key === 'colour_name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('size')}>
                  Size {sortBy.key === 'size' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('material_name')}>
                  Material {sortBy.key === 'material_name' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th role="button" onClick={() => toggleSort('created_at')} className="text-nowrap">
                  Created At {sortBy.key === 'created_at' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th style={{ width: 280 }} />
              </tr>
            </thead>

            <tbody>
              {paged.map((p) => (
                <tr key={p.id}>
                  <td className="text-monospace">{p.sku}</td>
                  <td>{p.customer_name || p.customer_id}</td>
                  <td>{p.brand_name || p.brand_id}</td>
                  <td>{p.category_name || p.category_id}</td>
                  <td>
                    <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                      <span>{p.colour_name || p.colour_id}</span>
                      {p.colour_hex && (
                        <span
                          title={p.colour_hex}
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 3,
                            border: '1px solid #ddd',
                            background: p.colour_hex,
                            display: 'inline-block'
                          }}
                        />
                      )}
                    </span>
                  </td>
                  <td>{renderSizeCell(p)}</td>
                  <td>{p.material_name || p.material_code || p.material_id}</td>
                  <td className="text-nowrap">
                    {p.created_at ? dayjs(p.created_at).format('DD MMM YYYY') : '-'}
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/app/sales/bagspa/products/${p.id}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      Detail
                    </Link>
                    <ManualDeleteButton kind="Produk" label={p.sku || `#${p.id}`} onDelete={() => ProductsAPI.destroy(p.id)} onDeleted={load} />
                  </td>
                </tr>
              ))}

              {paged.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bagspa-pagination-row">
          <div className="text-muted small">
            Total: {sorted.length} items • Page {pageSafe}/{totalPages}
          </div>

          <div className="btn-group">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe <= 1}
              onClick={() => setPage(1)}
            >
              &laquo;
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              &lsaquo;
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              &rsaquo;
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
